import { makeObservable, observable, action } from 'mobx';
import moment from 'moment';

import { Constructor } from 'jsx/mixins/types';

export interface InitializableType {
  hasInitialized: boolean;
  initialize: () => Promise<void>;
  initializedAt: moment.Moment | null | undefined;
  isDataStale: boolean;
  shouldInitialize: boolean;
}

const Initializable = <OnInitializeArguments extends unknown[]>(
  superclass: Constructor,
) => {
  // Stored outside the class so the in-flight promise doesn't appear in the
  // structural type, which would require object-literal mocks in tests to include it.
  const inflightMap = new WeakMap<object, Promise<void>>();

  abstract class InitializableClass extends superclass {
    @observable
    initializedAt: moment.Moment | null | undefined = null;

    constructor(...args: unknown[]) {
      super(...args);
      makeObservable(this);
    }

    protected abstract onInitialize(
      ...args: OnInitializeArguments
    ): Promise<unknown>;

    get isDataStale() {
      const hourAgo = moment().subtract(1, 'hours');

      return !!this.initializedAt && this.initializedAt.isBefore(hourAgo);
    }

    get hasInitialized() {
      return !!this.initializedAt;
    }

    get shouldInitialize() {
      return this.hasInitialized && !this.isDataStale;
    }

    @action
    initialize(...props: OnInitializeArguments) {
      if (this.shouldInitialize) {
        return Promise.resolve();
      }

      // If a fetch is already in flight, return the same promise to all concurrent
      // callers instead of firing a second request. Without this guard, multiple
      // components mounting simultaneously would each call onInitialize() because
      // shouldInitialize stays false until the first fetch resolves.
      const inflight = inflightMap.get(this);
      if (inflight) {
        return inflight;
      }

      const init = this.onInitialize(...props);

      // check to see if onInitialize returned a promise
      /* eslint-disable-next-line eqeqeq */
      if (Promise.resolve(init) == init) {
        const promise = (init as Promise<unknown>)
          .then(() => {
            this.initializedAt = moment();
            inflightMap.delete(this);
          })
          .catch((err: unknown) => {
            // Clear the in-flight entry on failure so the next initialize() call
            // can retry rather than returning the same rejected promise forever.
            inflightMap.delete(this);
            throw err;
          });
        inflightMap.set(this, promise);
        return promise;
      }

      this.initializedAt = moment();
      return Promise.resolve();
    }
  }

  return InitializableClass;
};

export default Initializable;
